import {
  Box,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  Title
} from '@mantine/core';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import type {
  ActivitySearchResult,
  Contract,
  ContractLine,
  WeekReport,
  WeekReportAttachment,
  WeekReportHour,
  WeekReportLine
} from '@/api/generated/models';
import type { CardTab, InfoField } from '@/components/common';
import { useListWeekReportActivitiesWithTotals } from '@/api/generated/activities/activities';
import {
  getListContractLinesQueryKey,
  useListContractLines,
  useListContracts
} from '@/api/generated/contracts/contracts';
import { WeekReportStatus } from '@/api/generated/models/weekReportStatus';
import { useListUsers } from '@/api/generated/users/users';
import {
  getGetWeekReportQueryKey,
  getListAttachmentsQueryKey,
  getListHoursQueryKey,
  getListWeekReportLinesQueryKey,
  getListWeekReportsQueryKey,
  useApproveWeekReport,
  useCreateCorrection,
  useDeleteWeekReport,
  useGetWeekReport,
  useListAttachments,
  useListHours,
  useListWeekReportLines,
  useMarkInReview,
  useRejectWeekReport,
  useRemoveWeekReportActivity,
  useStartReview,
  useSubmit
} from '@/api/generated/week-reports/week-reports';
import { getWorkPackage } from '@/api/generated/work-structure/work-structure';
import { IconFigmaArrowRight, IconFigmaXCircle } from '@/assets/icons';
import {
  AddActionButton,
  BackButton,
  CardTabs,
  ConfirmActionModal,
  ContentCard,
  InfoCard,
  ListPagination,
  ListTotalCard,
  MobileActionBar,
  MobileCard,
  MobileCardList,
  MobileCodeBadge,
  MobileField,
  MutationButton,
  StatusBanner,
  StatusPill,
  StatusTracker,
  TABLE_STATUS_COL_WIDTH,
  TABLE_WEEK_PRIMARY_COL_WIDTH,
  tableCenteredCellStyle,
  tableRowStyle,
  TotalAmountCell
} from '@/components/common';
import { activityLockState } from '@/features/activities/activityDisplay';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { InfoBulletList, PanelInfoIcon } from '@/features/board/components/PanelInfoIcon';
import { AddActivityToWeekReportModal } from '@/features/week-reports/components/AddActivityToWeekReportModal';
import { RejectWeekReportModal } from '@/features/week-reports/components/RejectWeekReportModal';
import { SubmitSuccessModal } from '@/features/week-reports/components/SubmitSuccessModal';
import {
  buildWeekReportTrackerSteps,
  formatWeekLabel,
  formatWeekPeriod,
  statusConfig
} from '@/features/week-reports/weekReportDisplay';
import { formatAmount, formatDate, otherPartyName } from '@/utils/format';
import { invalidateActivityQueries } from '@/utils/invalidateActivityQueries';
import { useLocalPagination } from '@/utils/useLocalPagination';
import { useSavedMessage } from '@/utils/useSavedMessage';

const ACTIVITY_AMOUNT_COL_WIDTH = 120;

const activityColumns: [string, number, ('center' | 'right' | undefined)?][] = [
  ['activities.colWeek', TABLE_WEEK_PRIMARY_COL_WIDTH],
  ['activities.colContract', 130],
  ['activities.colAmount', ACTIVITY_AMOUNT_COL_WIDTH, 'right'],
  ['activities.colWorkPackage', 200],
  ['activities.colStatus', TABLE_STATUS_COL_WIDTH, 'center']
];

export const WeekReportDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const queryClient = useQueryClient();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [submitSuccessOpen, setSubmitSuccessOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addActivityOpen, setAddActivityOpen] = useState(false);
  const [savedMessage, setSavedMessage] = useSavedMessage();
  const [activeTab, setActiveTab] = useState<'activities' | 'lines' | 'workers'>('activities');

  const { data, isLoading } = useGetWeekReport(id ?? '', {
    query: { refetchOnMount: 'always' }
  });
  const report = data as WeekReport | undefined;

  const { data: linesData } = useListWeekReportLines(id ?? '', {
    query: { refetchOnMount: 'always' }
  });
  const lines: WeekReportLine[] = linesData ?? [];

  const { data: attachmentsData } = useListAttachments(id ?? '');
  const attachments: WeekReportAttachment[] = attachmentsData ?? [];

  const { data: contractsData } = useListContracts();
  const contracts: Contract[] = contractsData ?? [];
  const contract = contracts.find((c) => c.id === report?.contractId);

  // contractLineId se NE prenosi na klijentovu kopiju week_report_lines (isto obrazloženje kao
  // plannedItemId — partnerov FK, nerazrješiv RLS-om), pa se na klijentovoj strani stavka
  // razrješava preko itemCode-a protiv klijentove VLASTITE kopije contract_lines.
  const { data: contractLinesData } = useListContractLines(report?.contractId ?? '', {
    query: { enabled: !!report?.contractId }
  });
  const contractLineById = useMemo(
    () => new Map((contractLinesData ?? []).map((cl) => [cl.id, cl])),
    [contractLinesData]
  );
  const contractLineByItemCode = useMemo(() => {
    const map = new Map<string, ContractLine>();
    (contractLinesData ?? []).forEach((cl) => {
      if (cl.itemCode) map.set(cl.itemCode, cl);
    });
    return map;
  }, [contractLinesData]);
  const resolveContractLine = (line: WeekReportLine): ContractLine | undefined => {
    if (line.contractLineId) return contractLineById.get(line.contractLineId);
    if (line.itemCode) return contractLineByItemCode.get(line.itemCode);
    return undefined;
  };

  const isPartnerView = !!user && !!contract && user.organisationId === contract.partnerOrgId;
  const isEditable =
    report?.status === WeekReportStatus.DRAFT ||
    report?.status === WeekReportStatus.IN_REVIEW ||
    report?.status === WeekReportStatus.CORRECTED;

  // Isti redoslijed kao listActivities(weekReportId), uz totalAmount po aktivnosti (kolona Iznos).
  const { data: activitiesData } = useListWeekReportActivitiesWithTotals(
    { weekReportId: id ?? '' },
    { query: { enabled: isPartnerView && !!id } }
  );
  const activities: ActivitySearchResult[] = isPartnerView ? (activitiesData ?? []) : [];

  const { data: hoursData } = useListHours(id ?? '', {
    query: { enabled: isPartnerView && !!id }
  });
  const hours: WeekReportHour[] = isPartnerView ? (hoursData ?? []) : [];

  const { data: usersData } = useListUsers(undefined, { query: { enabled: isPartnerView } });
  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    (usersData ?? []).forEach((u) => {
      if (u.id) map.set(u.id, `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim());
    });
    return map;
  }, [usersData]);

  const workerTotals = useMemo(() => {
    const totals = new Map<string, number>();
    hours.forEach((hour) => {
      if (!hour.userId) return;
      totals.set(hour.userId, (totals.get(hour.userId) ?? 0) + (hour.hours ?? 0));
    });
    return [...totals.entries()].map(([userId, totalHours]) => ({ userId, totalHours }));
  }, [hours]);

  const {
    page: activitiesPage,
    setPage: setActivitiesPage,
    pageSize: activitiesPageSize,
    setPageSize: setActivitiesPageSize,
    totalPages: activitiesTotalPages,
    pageItems: pageActivities
  } = useLocalPagination(activities);

  const {
    page: linesPage,
    setPage: setLinesPage,
    pageSize: linesPageSize,
    setPageSize: setLinesPageSize,
    totalPages: linesTotalPages,
    pageItems: pageLines,
    offset: linesOffset
  } = useLocalPagination(lines);

  const {
    page: workersPage,
    setPage: setWorkersPage,
    pageSize: workersPageSize,
    setPageSize: setWorkersPageSize,
    totalPages: workersTotalPages,
    pageItems: pageWorkerTotals,
    offset: workersOffset
  } = useLocalPagination(workerTotals);

  // Trake UKUPNO = zbroj TRENUTNE stranice (ne cijele liste).
  const totalActivitiesAmount = pageActivities.reduce(
    (sum, activity) => sum + (activity.totalAmount ?? 0),
    0
  );
  const totalWorkerHours = pageWorkerTotals.reduce((sum, worker) => sum + worker.totalHours, 0);

  const workPackageIds = useMemo(
    () => [
      ...new Set(
        activities.map((activity) => activity.workPackageId).filter((v): v is string => !!v)
      )
    ],
    [activities]
  );
  const workPackageQueries = useQueries({
    queries: workPackageIds.map((wpId) => ({
      queryKey: ['work-package', wpId],
      queryFn: () => getWorkPackage(wpId),
      enabled: !!wpId
    }))
  });
  const workPackageMap = new Map(
    workPackageIds.map((wpId, i) => [
      wpId,
      workPackageQueries[i]?.data as { name?: string } | undefined
    ])
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetWeekReportQueryKey(id) });
    queryClient.invalidateQueries({ queryKey: getListWeekReportsQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListWeekReportLinesQueryKey(id) });
    queryClient.invalidateQueries({ queryKey: getListAttachmentsQueryKey(id) });
    // approve() je JEDINO mjesto koje mijenja contract_lines.realized_qty/realized_amount
    // (vidi ContractLineDao.incrementRealized) — bez ovoga Ostatak na Stavke tabu ostaje
    // zastario dok se ekran ručno ne osvježi.
    if (report?.contractId) {
      queryClient.invalidateQueries({ queryKey: getListContractLinesQueryKey(report.contractId) });
    }
  };

  const invalidateActivityAggregates = () => {
    invalidate();
    queryClient.invalidateQueries({ queryKey: getListHoursQueryKey(id) });
    invalidateActivityQueries(queryClient);
  };

  const approveMutation = useApproveWeekReport({ mutation: { onSuccess: invalidate } });
  // skipGlobalErrorToast — ovaj poziv je opportunistički/u pozadini (vidi efekt ispod); ako
  // zakaže (npr. status je u međuvremenu već odmakao dalje), stvarni approve()/reject() poziv
  // odmah nakon njega će ionako ili uspjeti (status je već ispravan) ili sam prijaviti grešku
  // — ne treba dupli/zbunjujući toast samo za ovaj međukorak.
  const startReviewMutation = useStartReview({
    mutation: { onSuccess: invalidate, meta: { skipGlobalErrorToast: true } }
  });

  // SUBMITTED/RESUBMITTED → UNDER_REVIEW je obavezan korak prije approve()/reject() (vidi
  // WeekReportService.startReview() — "klijent signalizira da je aktivno počeo pregled"), ali
  // nema zaseban gumb za to u sučelju — klijent odmah vidi Odobri/Vrati na doradu. Okidač je
  // otvaranje ekrana od strane klijenta (best-effort, vidi efekt ispod) — ALI budući da je to
  // asinkrono i može kasniti, ensureUnderReview() se poziva i SINKRONO, izravno iz klik handlera
  // Odobri/Vrati na doradu (vidi niže), da popravak ne ovisi o pobjedi u utrci s mount-efektom.
  const startReviewTriggeredRef = useRef<string | null>(null);
  useEffect(() => {
    if (!report?.id || !contract || !user) return;
    if (user.organisationId !== contract.clientOrgId) return;
    if (
      report.status !== WeekReportStatus.SUBMITTED &&
      report.status !== WeekReportStatus.RESUBMITTED
    ) {
      return;
    }
    if (startReviewTriggeredRef.current === report.id) return;
    startReviewTriggeredRef.current = report.id;
    startReviewMutation.mutate({ id: report.id });
  }, [report?.id, report?.status, contract, user]);

  const ensureUnderReview = async () => {
    if (
      report?.id &&
      (report.status === WeekReportStatus.SUBMITTED ||
        report.status === WeekReportStatus.RESUBMITTED)
    ) {
      startReviewTriggeredRef.current = report.id;
      await startReviewMutation.mutateAsync({ id: report.id });
    }
  };

  const markInReviewMutation = useMarkInReview({ mutation: { onSuccess: invalidate } });
  const rejectMutation = useRejectWeekReport({
    mutation: {
      onSuccess: () => {
        invalidate();
        setRejectModalOpen(false);
      }
    }
  });
  const submitMutation = useSubmit({
    mutation: {
      onSuccess: () => {
        invalidate();
        setSubmitSuccessOpen(true);
      }
    }
  });
  const createCorrectionMutation = useCreateCorrection({
    mutation: {
      onSuccess: (correction) => {
        const correctionId = (correction as { id?: string })?.id;
        if (correctionId) navigate(`/weekly-reports/${correctionId}/correction`);
      }
    }
  });
  const deleteMutation = useDeleteWeekReport({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListWeekReportsQueryKey() });
        // Brisanje izvještaja otključava sve njegove aktivnosti (natrag na "Evidentirano").
        invalidateActivityQueries(queryClient);
        navigate('/weekly-reports');
      }
    }
  });
  const removeActivityMutation = useRemoveWeekReportActivity({
    mutation: { onSuccess: invalidateActivityAggregates }
  });

  if (isLoading || !report) {
    return (
      <Container size="lg" px={0}>
        <Center py={80}>
          <Loader size="sm" />
        </Center>
      </Container>
    );
  }

  const config = statusConfig[report.status ?? WeekReportStatus.DRAFT];
  const trackerSteps = buildWeekReportTrackerSteps(report.status ?? WeekReportStatus.DRAFT);
  const isApproved = report.status === WeekReportStatus.APPROVED;
  const isDraft = report.status === WeekReportStatus.DRAFT;
  const canAct =
    (report.status === WeekReportStatus.SUBMITTED ||
      report.status === WeekReportStatus.UNDER_REVIEW) &&
    !!user &&
    !!contract &&
    user.organisationId === contract.clientOrgId;
  // OFF-NEW obrazac repliciran na Tjedni izvještaj: Nacrt → Interno odobreno (markInReview) →
  // Predan (submit) — submit() sad zahtijeva IN_REVIEW, ne više izravno DRAFT.
  const canMarkInReview =
    isDraft && !!user && !!contract && user.organisationId === contract.partnerOrgId;
  const canSubmit =
    report.status === WeekReportStatus.IN_REVIEW &&
    !!user &&
    !!contract &&
    user.organisationId === contract.partnerOrgId;
  const canCorrect =
    report.status === WeekReportStatus.REJECTED &&
    !!user &&
    !!contract &&
    user.organisationId === contract.partnerOrgId;
  const canDelete = isPartnerView && isEditable;

  const infoFields: InfoField[] = [
    {
      label: t('weekReportDetail.kpiContract'),
      value: contract?.contractNumber ?? '—',
      ...(report.contractId ? { onClick: () => navigate(`/contracts/${report.contractId}`) } : {})
    },
    {
      label: t('weekReportDetail.kpiPartner'),
      value: contract ? otherPartyName(contract, user?.orgName) : '—'
    },
    {
      label: isDraft ? t('weekReportDetail.kpiLastSaved') : t('weekReportDetail.kpiSentDate'),
      value: formatDate(report.updatedAt)
    },
    ...(isApproved
      ? [{ label: t('weekReportDetail.kpiApprovedDate'), value: formatDate(report.updatedAt) }]
      : [])
  ];

  const handleApprove = async () => {
    if (!id) return;
    await ensureUnderReview();
    approveMutation.mutate({ id });
  };

  const handleReject = async (reason: string) => {
    if (!id) return;
    await ensureUnderReview();
    rejectMutation.mutate({ id, data: { reason } });
  };

  const actionButtons = (
    <>
      <Button
        variant="outline"
        onClick={() => setRejectModalOpen(true)}
        styles={{ root: { color: 'var(--color-text-secondary)' } }}
      >
        {t('weekReportDetail.returnForRework')}
      </Button>
      <MutationButton
        isPending={approveMutation.isPending || startReviewMutation.isPending}
        isPaused={approveMutation.isPaused}
        onClick={() => void handleApprove()}
      >
        {t('weekReportDetail.approve')}
      </MutationButton>
    </>
  );

  const markInReviewButton = (
    <MutationButton
      variant="outline"
      isPending={markInReviewMutation.isPending}
      isPaused={markInReviewMutation.isPaused}
      onClick={() => id && markInReviewMutation.mutate({ id })}
    >
      {t('weekReportDetail.markInReview')}
    </MutationButton>
  );

  const submitButton = (
    <MutationButton
      isPending={submitMutation.isPending}
      isPaused={submitMutation.isPaused}
      onClick={() => id && submitMutation.mutate({ id })}
      rightSection={<IconFigmaArrowRight size={18} />}
    >
      {t('weekReportDetail.submit')}
    </MutationButton>
  );

  const correctButton = (
    <MutationButton
      isPending={createCorrectionMutation.isPending}
      isPaused={createCorrectionMutation.isPaused}
      onClick={() => id && createCorrectionMutation.mutate({ id })}
      rightSection={<IconFigmaArrowRight size={18} />}
    >
      {t('weekReportDetail.correct')}
    </MutationButton>
  );

  const deleteButton = (
    <Button color="red" variant="outline" onClick={() => setDeleteModalOpen(true)}>
      {t('weekReportDetail.deleteReport')}
    </Button>
  );

  // Partner: EVIDENTIRANO = živi zbroj svih aktivnosti na liniji ugovora (recordedQty,
  // uključuje i ovaj izvještaj) — isto kao na aktivnosti/radnom paketu. Klijent ne vidi
  // aktivnosti (RLS, recordedQty mu je uvijek 0), pa mu ostaje REALIZIRANO = odobreno
  // (realizedQty, raste tek njegovim odobrenjem izvještaja).
  const lineMetrics = (line: (typeof lines)[number]) => {
    const contractLine = resolveContractLine(line);
    const shownQty = isPartnerView
      ? (contractLine?.recordedQty ?? 0)
      : (contractLine?.realizedQty ?? 0);
    const shownValue = isPartnerView
      ? shownQty * (contractLine?.unitPrice ?? 0)
      : (contractLine?.realizedAmount ?? 0);
    const remainingQty = contractLine != null ? (contractLine.quantity ?? 0) - shownQty : undefined;
    const remainingValue =
      contractLine != null ? (contractLine.totalPrice ?? 0) - shownValue : undefined;
    return { contractLine, shownQty, shownValue, remainingQty, remainingValue };
  };

  // Iznos stavke OVOG izvještaja (ostvarena količina × cijena u trenutku izvještaja) — jedini iznos
  // koji je isti na partnerovoj i klijentovoj kopiji; Evidentirano/Realizirano su stanje cijelog ugovora.
  const reportLineAmount = (line: (typeof lines)[number]) =>
    line.lineAmount ?? (line.realizedQty ?? 0) * (line.snapshotPrice ?? 0);

  // Traka UKUPNO ispod stavki — samo iznosi, zbroj TRENUTNE stranice. Stupci su vrijednosti cijele
  // stavke ugovora, pa se svaka stavka ugovora broji jednom.
  const lineTotals = (() => {
    const seen = new Set<string>();
    let contracted = 0;
    let shown = 0;
    pageLines.forEach((line) => {
      const { contractLine, shownValue } = lineMetrics(line);
      if (!contractLine?.id || seen.has(contractLine.id)) return;
      seen.add(contractLine.id);
      contracted += contractLine.totalPrice ?? 0;
      shown += shownValue;
    });
    const report = pageLines.reduce((sum, line) => sum + reportLineAmount(line), 0);
    return { contracted, shown, remaining: contracted - shown, report };
  })();

  const linesContent = (
    <div>
      <Box visibleFrom="sm" style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 900 }}>
          <div
            className="table-header-row"
            style={{
              ...tableRowStyle,
              padding: '12px 20px',
              backgroundColor: 'var(--color-bg-canvas)',
              borderBottom: '1px solid var(--color-border-soft)'
            }}
          >
            <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)', width: 32 }}>
              {t('activityDetail.colIndex')}
            </Text>
            <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)', width: 90 }}>
              {t('activityDetail.colCode')}
            </Text>
            <Text fz={10} fw={600} style={{ color: 'var(--color-text-muted)', flex: 1 }}>
              {t('activityDetail.colDescription')}
            </Text>
            <Text
              fz={10}
              fw={600}
              ta="right"
              style={{ color: 'var(--color-text-muted)', width: 110 }}
            >
              {t('activityDetail.colRealizedQty')}
            </Text>
            <Text
              fz={10}
              fw={600}
              ta="center"
              style={{ color: 'var(--color-text-muted)', width: 70 }}
            >
              {t('activityDetail.colUnit')}
            </Text>
            <Text
              fz={10}
              fw={600}
              ta="right"
              style={{ color: 'var(--color-text-muted)', width: 100 }}
            >
              {t('activityDetail.colContracted')}
            </Text>
            <Text
              fz={10}
              fw={600}
              ta="right"
              style={{ color: 'var(--color-text-muted)', width: 100 }}
            >
              {t(isPartnerView ? 'activityDetail.colRecorded' : 'activityDetail.colRealizedTotal')}
            </Text>
            <Text
              fz={10}
              fw={600}
              ta="right"
              style={{ color: 'var(--color-text-muted)', width: 100 }}
            >
              {t('activityDetail.colRemaining')}
            </Text>
          </div>

          {pageLines.map((line, index) => {
            const { contractLine, shownQty, shownValue, remainingQty, remainingValue } =
              lineMetrics(line);

            return (
              <div
                key={line.id}
                style={{
                  ...tableRowStyle,
                  padding: '12px 20px',
                  borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                }}
              >
                <Text fz={12} style={{ color: 'var(--color-text-muted)', width: 32 }}>
                  {linesOffset + index + 1}
                </Text>
                <Text fz={12} style={{ color: 'var(--color-text-primary)', width: 90 }}>
                  {line.itemCode ?? '—'}
                </Text>
                <Text fz={13.5} style={{ color: 'var(--color-text-secondary)', flex: 1 }}>
                  {line.description}
                </Text>
                <Stack gap={2} style={{ width: 110, alignItems: 'flex-end' }}>
                  <Text
                    fz={13.5}
                    fw={700}
                    ta="right"
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {line.realizedQty ?? '—'}
                  </Text>
                  <Text fz={11} ta="right" style={{ color: 'var(--color-text-muted)' }}>
                    {formatAmount(reportLineAmount(line))}
                  </Text>
                </Stack>
                <Text
                  fz={12}
                  ta="center"
                  style={{ color: 'var(--color-text-secondary)', width: 70 }}
                >
                  {line.unit ?? '—'}
                </Text>
                <Stack gap={2} style={{ width: 100, alignItems: 'flex-end' }}>
                  <Text fz={13.5} ta="right" style={{ color: 'var(--color-text-secondary)' }}>
                    {contractLine?.quantity ?? '—'}
                  </Text>
                  {contractLine && (
                    <Text fz={11} ta="right" style={{ color: 'var(--color-text-muted)' }}>
                      {formatAmount(contractLine.totalPrice ?? 0)}
                    </Text>
                  )}
                </Stack>
                <Stack gap={2} style={{ width: 100, alignItems: 'flex-end' }}>
                  <Text fz={13.5} ta="right" style={{ color: 'var(--color-text-secondary)' }}>
                    {contractLine ? shownQty : '—'}
                  </Text>
                  {contractLine && (
                    <Text fz={11} ta="right" style={{ color: 'var(--color-text-muted)' }}>
                      {formatAmount(shownValue)}
                    </Text>
                  )}
                </Stack>
                <Stack gap={2} style={{ width: 100, alignItems: 'flex-end' }}>
                  <Text
                    fz={13.5}
                    fw={600}
                    ta="right"
                    style={{
                      color:
                        remainingQty != null && remainingQty < 0
                          ? 'var(--color-status-danger)'
                          : 'var(--color-text-primary)'
                    }}
                  >
                    {remainingQty ?? '—'}
                  </Text>
                  {contractLine && (
                    <Text
                      fz={11}
                      ta="right"
                      style={{
                        color:
                          remainingValue != null && remainingValue < 0
                            ? 'var(--color-status-danger)'
                            : 'var(--color-text-muted)'
                      }}
                    >
                      {formatAmount(remainingValue ?? 0)}
                    </Text>
                  )}
                </Stack>
              </div>
            );
          })}
          {lines.length > 0 && (
            <div
              style={{
                ...tableRowStyle,
                padding: '14px 20px',
                borderTop: '2px solid var(--color-border-default)',
                backgroundColor: 'var(--color-bg-canvas)'
              }}
            >
              <Text fz={13} fw={700} style={{ color: 'var(--color-text-primary)', flex: 1 }}>
                {t('weekReportDetail.totalHours')}
              </Text>
              <TotalAmountCell width={110} amount={lineTotals.report} />
              <div style={{ width: 70 }} />
              <TotalAmountCell width={100} amount={lineTotals.contracted} />
              <TotalAmountCell width={100} amount={lineTotals.shown} />
              <TotalAmountCell width={100} amount={lineTotals.remaining} />
            </div>
          )}
        </div>
      </Box>

      <MobileCardList>
        {pageLines.map((line) => {
          const { contractLine, shownQty, remainingQty, remainingValue } = lineMetrics(line);
          const isOver = remainingQty != null && remainingQty < 0;
          return (
            <MobileCard key={line.id}>
              <Group justify="space-between" align="center" wrap="nowrap" gap={8}>
                <MobileCodeBadge>
                  {t('common.codeBadge', { code: line.itemCode ?? '—' })}
                </MobileCodeBadge>
                <Text
                  fz={15}
                  fw={700}
                  style={{ color: 'var(--color-text-primary)', flexShrink: 0 }}
                >
                  {line.realizedQty ?? '—'} {line.unit ?? ''} ·{' '}
                  {formatAmount(reportLineAmount(line))}
                </Text>
              </Group>
              <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                {line.description}
              </Text>
              {contractLine && (
                <Stack gap={4}>
                  <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
                    {t(isPartnerView ? 'common.recorded' : 'common.realized')} {shownQty} /{' '}
                    {contractLine.quantity ?? '—'} {line.unit ?? ''}
                  </Text>
                  <Progress
                    size={4}
                    radius="xl"
                    value={
                      contractLine.quantity
                        ? Math.min(100, (shownQty / contractLine.quantity) * 100)
                        : 0
                    }
                    color={isOver ? 'var(--color-status-danger)' : 'var(--color-accent-primary)'}
                    styles={{ root: { backgroundColor: 'var(--color-bg-surface-2)' } }}
                  />
                  <Text
                    fz={12.5}
                    fw={600}
                    style={{
                      color: isOver ? 'var(--color-status-danger)' : 'var(--color-text-primary)'
                    }}
                  >
                    {t('common.remaining')} {remainingQty ?? '—'} {line.unit ?? ''} ·{' '}
                    {formatAmount(remainingValue ?? 0)}
                  </Text>
                </Stack>
              )}
            </MobileCard>
          );
        })}
        {lines.length > 0 && (
          <ListTotalCard
            amount={lineTotals.report}
            details={[
              { label: t('activityDetail.colContracted'), amount: lineTotals.contracted },
              {
                label: t(
                  isPartnerView ? 'activityDetail.colRecorded' : 'activityDetail.colRealizedTotal'
                ),
                amount: lineTotals.shown
              },
              { label: t('activityDetail.colRemaining'), amount: lineTotals.remaining }
            ]}
          />
        )}
      </MobileCardList>

      {lines.length === 0 && (
        <Center py={40}>
          <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
            {t('weekReportDetail.noLines')}
          </Text>
        </Center>
      )}

      {lines.length > 0 && (
        <ListPagination
          label={t('weekReportDetail.linesShowing', {
            shown: pageLines.length,
            total: lines.length
          })}
          page={linesPage}
          totalPages={linesTotalPages}
          onPageChange={setLinesPage}
          pageSize={linesPageSize}
          onPageSizeChange={setLinesPageSize}
        />
      )}
    </div>
  );

  const activitiesTab: CardTab = {
    key: 'activities',
    label: t('weekReportDetail.activitiesTitle'),
    caption: t('weekReportDetail.activitiesCaption'),
    headerRight: isEditable && (
      <AddActionButton onClick={() => setAddActivityOpen(true)}>
        {t('weekReportDetail.addActivityButton')}
      </AddActionButton>
    ),
    content: (
      <div>
        <Box visibleFrom="sm" style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 980 + ACTIVITY_AMOUNT_COL_WIDTH }}>
            <div
              className="table-header-row"
              style={{
                ...tableRowStyle,
                padding: '12px 20px',
                backgroundColor: 'var(--color-bg-canvas)',
                borderBottom: '1px solid var(--color-border-soft)'
              }}
            >
              {activityColumns.map(([key, width, align]) => (
                <Text
                  key={key}
                  fz={10}
                  fw={600}
                  w={width}
                  ta={align}
                  style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                >
                  {t(key)}
                </Text>
              ))}
              <Text
                fz={10}
                fw={600}
                w={100}
                ta="right"
                style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
              >
                {t('activities.colAction')}
              </Text>
              {isEditable && (
                <Text
                  fz={10}
                  fw={600}
                  w={40}
                  ta="right"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {' '}
                </Text>
              )}
            </div>

            {pageActivities.map((activity, index) => {
              const status = activityLockState(activity.weekReportId);
              const workPackageName = activity.workPackageId
                ? (workPackageMap.get(activity.workPackageId)?.name ?? '—')
                : t('activities.directFromContract');

              return (
                <div
                  key={activity.id}
                  style={{
                    ...tableRowStyle,
                    padding: '14px 20px',
                    borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                  }}
                >
                  <Text
                    fz={13.5}
                    fw={600}
                    w={TABLE_WEEK_PRIMARY_COL_WIDTH}
                    style={{ color: 'var(--color-text-primary)' }}
                  >
                    {formatWeekLabel(activity.weekNumber, activity.reportYear)}
                  </Text>
                  <Text fz={13} w={130} style={{ color: 'var(--color-text-secondary)' }}>
                    {contract?.contractNumber ?? '—'}
                  </Text>
                  <Text
                    fz={13}
                    w={ACTIVITY_AMOUNT_COL_WIDTH}
                    ta="right"
                    style={{ color: 'var(--color-text-secondary)' }}
                  >
                    {formatAmount(activity.totalAmount)}
                  </Text>
                  <Text fz={13} w={200} style={{ color: 'var(--color-text-secondary)' }} truncate>
                    {workPackageName}
                  </Text>
                  <div style={{ ...tableCenteredCellStyle, width: TABLE_STATUS_COL_WIDTH }}>
                    <StatusPill label={t(status.labelKey)} bg={status.bg} fg={status.fg} />
                  </div>
                  <Text
                    fz={12}
                    fw={600}
                    w={100}
                    ta="right"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/activities/${activity.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        navigate(`/activities/${activity.id}`);
                      }
                    }}
                    style={{ color: 'var(--color-accent-primary-active)', cursor: 'pointer' }}
                  >
                    {t('activities.details')}
                  </Text>
                  {isEditable && (
                    <Group w={40} justify="flex-end">
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (id && activity.id) {
                            removeActivityMutation.mutate({ id, activityId: activity.id });
                          }
                        }}
                        style={{ cursor: 'pointer', color: 'var(--color-status-danger)' }}
                      >
                        <IconFigmaXCircle size={16} />
                      </Box>
                    </Group>
                  )}
                </div>
              );
            })}

            {activities.length > 0 && (
              <div
                style={{
                  ...tableRowStyle,
                  padding: '14px 20px',
                  borderTop: '2px solid var(--color-border-default)',
                  backgroundColor: 'var(--color-bg-canvas)'
                }}
              >
                <Text
                  fz={13}
                  fw={700}
                  w={TABLE_WEEK_PRIMARY_COL_WIDTH}
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {t('weekReportDetail.activitiesTotal')}
                </Text>
                <div style={{ width: 130 }} />
                <Text
                  fz={13}
                  fw={700}
                  w={ACTIVITY_AMOUNT_COL_WIDTH}
                  ta="right"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {formatAmount(totalActivitiesAmount)}
                </Text>
                <div style={{ width: 200 }} />
                <div style={{ width: TABLE_STATUS_COL_WIDTH }} />
                <div style={{ width: 100 }} />
                {isEditable && <div style={{ width: 40 }} />}
              </div>
            )}
          </div>
        </Box>

        <MobileCardList>
          {pageActivities.map((activity) => {
            const status = activityLockState(activity.weekReportId);
            const workPackageName = activity.workPackageId
              ? (workPackageMap.get(activity.workPackageId)?.name ?? '—')
              : t('activities.directFromContract');
            const openActivity = () => navigate(`/activities/${activity.id}`);
            return (
              <MobileCard key={activity.id}>
                <Group justify="space-between" align="flex-start" wrap="nowrap" gap={8}>
                  <MobileField label={t('activities.colWeek')}>
                    <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                      {formatWeekLabel(activity.weekNumber, activity.reportYear)}
                    </Text>
                  </MobileField>
                  {isEditable && (
                    <Box
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (id && activity.id) {
                          removeActivityMutation.mutate({ id, activityId: activity.id });
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        color: 'var(--color-status-danger)',
                        flexShrink: 0
                      }}
                    >
                      <IconFigmaXCircle size={18} />
                    </Box>
                  )}
                </Group>
                <SimpleGrid cols={2} spacing={8}>
                  <MobileField label={t('activities.colContract')}>
                    <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                      {contract?.contractNumber ?? '—'}
                    </Text>
                  </MobileField>
                  <MobileField label={t('activities.colWorkPackage')}>
                    <Text fz={13} style={{ color: 'var(--color-text-secondary)' }}>
                      {workPackageName}
                    </Text>
                  </MobileField>
                  <MobileField label={t('activities.colAmount')}>
                    <Text fz={13} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                      {formatAmount(activity.totalAmount)}
                    </Text>
                  </MobileField>
                </SimpleGrid>
                <Group justify="space-between" align="flex-end" wrap="nowrap" gap={8}>
                  <MobileField label={t('activities.colStatus')}>
                    <StatusPill label={t(status.labelKey)} bg={status.bg} fg={status.fg} />
                  </MobileField>
                  <Text
                    fz={13}
                    fw={600}
                    role="button"
                    tabIndex={0}
                    onClick={openActivity}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') openActivity();
                    }}
                    style={{ color: 'var(--color-accent-primary-active)', cursor: 'pointer' }}
                  >
                    {t('activities.details')}
                  </Text>
                </Group>
              </MobileCard>
            );
          })}
          {activities.length > 0 && (
            <Group
              justify="space-between"
              align="center"
              wrap="nowrap"
              p={12}
              style={{ borderRadius: 8, backgroundColor: 'var(--color-bg-surface-2)' }}
            >
              <Text fz={13.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                {t('weekReportDetail.activitiesTotal')}
              </Text>
              <Text fz={15} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                {formatAmount(totalActivitiesAmount)}
              </Text>
            </Group>
          )}
        </MobileCardList>

        {activities.length === 0 && (
          <Center py={40}>
            <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
              {t('weekReportDetail.noActivities')}
            </Text>
          </Center>
        )}

        {activities.length > 0 && (
          <ListPagination
            label={t('weekReportDetail.activitiesShowing', {
              shown: pageActivities.length,
              total: activities.length
            })}
            page={activitiesPage}
            totalPages={activitiesTotalPages}
            onPageChange={setActivitiesPage}
            pageSize={activitiesPageSize}
            onPageSizeChange={setActivitiesPageSize}
          />
        )}
      </div>
    )
  };

  const linesTab: CardTab = {
    key: 'lines',
    label: t('weekReportDetail.linesTitle'),
    caption: t('weekReportDetail.linesCaption'),
    // Partner vidi Evidentirano, klijent Realizirano (odobreno) — vidi kolone ispod.
    captionInfo: isPartnerView ? (
      <PanelInfoIcon
        width={360}
        label={
          <InfoBulletList
            intro={t('columnsInfo.intro')}
            items={[
              t('columnsInfo.contracted'),
              t('columnsInfo.recorded'),
              t('columnsInfo.remaining'),
              t('columnsInfo.amounts')
            ]}
          />
        }
      />
    ) : (
      <PanelInfoIcon
        width={360}
        label={
          <InfoBulletList
            intro={t('columnsInfo.intro')}
            items={[
              t('columnsInfo.contracted'),
              t('columnsInfo.realizedApproved'),
              t('columnsInfo.remainingApproved'),
              t('columnsInfo.amounts')
            ]}
          />
        }
      />
    ),
    content: linesContent
  };

  const workersTab: CardTab = {
    key: 'workers',
    label: t('weekReportDetail.workersTitle'),
    caption: t('weekReportDetail.workersCaption'),
    content: (
      <div>
        <Box visibleFrom="sm">
          <div
            className="table-header-row"
            style={{
              ...tableRowStyle,
              padding: '12px 20px',
              backgroundColor: 'var(--color-bg-canvas)',
              borderBottom: '1px solid var(--color-border-soft)'
            }}
          >
            <Text
              fz={10}
              fw={600}
              style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4, width: 32 }}
            >
              {t('weekReportDetail.colIndex')}
            </Text>
            <Text
              fz={10}
              fw={600}
              style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4, flex: 1 }}
            >
              {t('weekReportDetail.colWorker')}
            </Text>
            <Text
              fz={10}
              fw={600}
              ta="right"
              style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4, width: 90 }}
            >
              {t('weekReportDetail.colHours')}
            </Text>
          </div>

          {pageWorkerTotals.map((worker, index) => (
            <div
              key={worker.userId}
              style={{
                ...tableRowStyle,
                padding: '14px 20px',
                borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
              }}
            >
              <Text fz={13} style={{ color: 'var(--color-text-muted)', width: 32 }}>
                {workersOffset + index + 1}
              </Text>
              <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)', flex: 1 }}>
                {userMap.get(worker.userId) ?? '—'}
              </Text>
              <Text
                fz={13.5}
                fw={600}
                ta="right"
                style={{ color: 'var(--color-text-primary)', width: 90 }}
              >
                {worker.totalHours}
              </Text>
            </div>
          ))}

          {workerTotals.length > 0 && (
            <div
              style={{
                ...tableRowStyle,
                padding: '14px 20px',
                borderTop: '2px solid var(--color-border-default)',
                backgroundColor: 'var(--color-bg-canvas)'
              }}
            >
              <Text fz={13.5} fw={700} style={{ color: 'var(--color-text-primary)', flex: 1 }}>
                {t('weekReportDetail.totalHours')}
              </Text>
              <Text
                fz={14}
                fw={700}
                ta="right"
                style={{ color: 'var(--color-text-primary)', width: 90 }}
              >
                {totalWorkerHours}
              </Text>
            </div>
          )}
        </Box>

        {workerTotals.length === 0 && (
          <Center py={40}>
            <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
              {t('weekReportDetail.noWorkers')}
            </Text>
          </Center>
        )}

        <MobileCardList>
          {pageWorkerTotals.map((worker, index) => (
            <MobileCard key={worker.userId}>
              <Group justify="space-between" align="center" wrap="nowrap" gap={8}>
                <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                  <Text fz={12} style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}>
                    {workersOffset + index + 1}.
                  </Text>
                  <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                    {userMap.get(worker.userId) ?? '—'}
                  </Text>
                </Group>
                <MobileField label={t('weekReportDetail.colHours')} align="right">
                  <Text fz={15} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                    {worker.totalHours}
                  </Text>
                </MobileField>
              </Group>
            </MobileCard>
          ))}
          {workerTotals.length > 0 && (
            <Group
              justify="space-between"
              align="center"
              wrap="nowrap"
              p={12}
              style={{ borderRadius: 8, backgroundColor: 'var(--color-bg-surface-2)' }}
            >
              <Text fz={13.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                {t('weekReportDetail.totalHours')}
              </Text>
              <Text fz={15} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                {totalWorkerHours}
              </Text>
            </Group>
          )}
        </MobileCardList>

        {workerTotals.length > 0 && (
          <ListPagination
            label={t('weekReportDetail.workersShowing', {
              shown: pageWorkerTotals.length,
              total: workerTotals.length
            })}
            page={workersPage}
            totalPages={workersTotalPages}
            onPageChange={setWorkersPage}
            pageSize={workersPageSize}
            onPageSizeChange={setWorkersPageSize}
          />
        )}
      </div>
    )
  };

  const tabs: CardTab[] = [
    ...(isPartnerView ? [activitiesTab] : []),
    linesTab,
    ...(isPartnerView ? [workersTab] : [])
  ];

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to="/weekly-reports" />

        {savedMessage && (
          <StatusBanner
            tone="success"
            message={savedMessage}
            onDismiss={() => setSavedMessage(null)}
          />
        )}

        {isApproved && (
          <StatusBanner tone="success" message={t('weekReportDetail.approvedBanner')} />
        )}

        <Group justify="space-between" align="flex-start" wrap="wrap" visibleFrom="sm">
          <Stack gap={4}>
            <Group gap={8} wrap="wrap">
              <Title order={2} fz={24} style={{ color: 'var(--color-text-primary)' }}>
                {t('weekReportDetail.title', { week: report.weekNumber })}
              </Title>
              <StatusPill label={t(config.labelKey)} bg={config.bg} fg={config.fg} header />
            </Group>
            <Text fz={14} style={{ color: 'var(--color-text-secondary)' }}>
              {t('weekReportDetail.period')}{' '}
              <Text span inherit fw={600} style={{ color: 'var(--color-text-primary)' }}>
                {formatWeekPeriod(report.weekNumber, report.reportYear)}
              </Text>
            </Text>
          </Stack>

          <Group gap={12}>
            {canAct && actionButtons}
            {canMarkInReview && markInReviewButton}
            {canSubmit && submitButton}
            {canCorrect && correctButton}
            {canDelete && deleteButton}
          </Group>
        </Group>

        <Stack gap={12} hiddenFrom="sm" style={{ width: '100%' }}>
          <Stack gap={8}>
            <Title order={2} fz={22} style={{ color: 'var(--color-text-primary)' }}>
              {t('weekReportDetail.title', { week: report.weekNumber })}
            </Title>
            <StatusPill label={t(config.labelKey)} bg={config.bg} fg={config.fg} header />
          </Stack>
          <Text fz={13.5} style={{ color: 'var(--color-text-secondary)' }}>
            {t('weekReportDetail.period')}{' '}
            <Text span inherit fw={600} style={{ color: 'var(--color-text-primary)' }}>
              {formatWeekPeriod(report.weekNumber, report.reportYear)}
            </Text>
          </Text>
          <MobileActionBar>
            {canAct && actionButtons}
            {canMarkInReview && markInReviewButton}
            {canSubmit && submitButton}
            {canCorrect && correctButton}
            {canDelete && deleteButton}
          </MobileActionBar>
        </Stack>

        <StatusTracker steps={trackerSteps} />

        <InfoCard
          fields={infoFields}
          columns={infoFields.length}
          borderColor="var(--color-border-default)"
        />

        <Box style={{ overflowX: 'auto', width: '100%' }}>
          <CardTabs
            tabs={tabs}
            active={activeTab}
            onChange={(key) => setActiveTab(key as typeof activeTab)}
          />
        </Box>

        {attachments.length > 0 && (
          <ContentCard style={{ padding: 20 }}>
            <Stack gap={16}>
              <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                {t('weekReportDetail.attachments')}
              </Text>
              <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 16, width: '100%' }}>
                {attachments.map((attachment) => (
                  <Group
                    key={attachment.id}
                    justify="space-between"
                    wrap="nowrap"
                    p={16}
                    style={{
                      flex: '1 1 260px',
                      minWidth: 240,
                      borderRadius: 6,
                      border: '1px solid var(--color-border-soft)',
                      backgroundColor: 'var(--color-bg-canvas)'
                    }}
                  >
                    <Stack gap={2} style={{ minWidth: 0 }}>
                      <Text
                        fz={13}
                        fw={600}
                        style={{ color: 'var(--color-text-primary)' }}
                        truncate
                      >
                        {attachment.fileName}
                      </Text>
                      <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                        {attachment.fileSizeKb
                          ? `${(attachment.fileSizeKb / 1024).toFixed(1)} MB`
                          : ''}
                      </Text>
                    </Stack>
                    {attachment.fileUrl && (
                      <a
                        href={attachment.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--color-accent-primary-active)',
                          textDecoration: 'underline',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {t('weekReportDetail.download')}
                      </a>
                    )}
                  </Group>
                ))}
              </Box>
            </Stack>
          </ContentCard>
        )}
      </Stack>

      <RejectWeekReportModal
        opened={rejectModalOpen}
        weekNumber={report.weekNumber ?? 0}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={(reason) => void handleReject(reason)}
        loading={rejectMutation.isPending || startReviewMutation.isPending}
        isPaused={rejectMutation.isPaused}
      />

      <SubmitSuccessModal
        opened={submitSuccessOpen}
        weekNumber={report.weekNumber ?? 0}
        period={formatWeekPeriod(report.weekNumber, report.reportYear)}
        recipient={contract ? otherPartyName(contract, user?.orgName) : '—'}
        onClose={() => {
          setSubmitSuccessOpen(false);
          navigate('/weekly-reports');
        }}
      />

      <ConfirmActionModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => id && deleteMutation.mutate({ id })}
        title={t('weekReportDetail.deleteConfirmTitle')}
        message={t('weekReportDetail.deleteConfirmMessage', { week: report.weekNumber })}
        confirmLabel={t('common.delete')}
        danger
        loading={deleteMutation.isPending}
        isPaused={deleteMutation.isPaused}
      />

      {report.contractId && (
        <AddActivityToWeekReportModal
          opened={addActivityOpen}
          onClose={() => setAddActivityOpen(false)}
          weekReportId={id ?? ''}
          contractId={report.contractId}
          weekNumber={report.weekNumber ?? 0}
          reportYear={report.reportYear ?? 0}
        />
      )}
    </Container>
  );
};
